import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createAdjustment, apiRequest } from "@/lib/api";
import { getCurrentUser, getCurrentShop } from "@/lib/auth";
import { Loader2, Search } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const ADJUSTMENT_REASONS = [
  "Faltante no identificado",
  "Sobrante no identificado",
  "Error de conteo anterior",
  "Ajuste por inventario físico",
  "Otro",
];

export function AdjustmentModal({
  show,
  onClose,
  onSuccess,
}: {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const currentShop = getCurrentShop()!;
  const currentUser = getCurrentUser();

  const [productSearch, setProductSearch] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearch, setShowSearch] = useState(false);

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [physicalStock, setPhysicalStock] = useState<number>(0);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  const resetForm = () => {
    setProductSearch("");
    setSearchResults([]);
    setShowSearch(false);
    setSelectedItem(null);
    setPhysicalStock(0);
    setReason("");
    setNotes("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const searchProducts = async (q: string) => {
    setProductSearch(q);
    if (q.length < 1) { setSearchResults([]); return; }
    try {
      const data = await apiRequest<any>(
        `/api/shops/${currentShop.id}/inventory?search=${encodeURIComponent(q)}&limit=10`,
      );
      setSearchResults(Array.isArray(data) ? data : data?.data || []);
    } catch { setSearchResults([]); }
  };

  const selectProduct = (item: any) => {
    setSelectedItem(item);
    setPhysicalStock(item.stock);
    setShowSearch(false);
    setProductSearch("");
    setSearchResults([]);
  };

  const difference = selectedItem ? physicalStock - selectedItem.stock : 0;
  const avgCost = parseFloat(selectedItem?.averageCost || selectedItem?.acquisitionCost || "0");
  const diffValue = Math.abs(difference) * avgCost;

  const handleSave = async () => {
    if (!selectedItem) {
      toast({ title: "Debe seleccionar un producto", variant: "destructive" });
      return;
    }
    if (physicalStock < 0) {
      toast({ title: "El stock físico no puede ser negativo", variant: "destructive" });
      return;
    }
    if (difference === 0) {
      toast({ title: "El stock físico coincide con el sistema", variant: "destructive" });
      return;
    }
    if (!reason) {
      toast({ title: "Debe seleccionar un motivo", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      await createAdjustment(currentShop.id, {
        inventoryId: selectedItem.id,
        productId: selectedItem.productId,
        physicalStock,
        reason,
        notes: notes || undefined,
        registeredById: currentUser?.id,
      });

      toast({ title: "Ajuste registrado exitosamente" });
      resetForm();
      onSuccess();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={show} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>🟡 Ajuste de Inventario</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Product selector */}
          <div className="space-y-2">
            <Label>Producto *</Label>
            <div className="relative">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={productSearch}
                    onChange={(e) => searchProducts(e.target.value)}
                    onFocus={() => setShowSearch(true)}
                    className="pl-9"
                    placeholder="Buscar producto..."
                  />
                </div>
              </div>
              {showSearch && productSearch.length > 0 && (
                <div className="absolute z-10 w-full mt-1 border rounded-lg bg-background shadow-lg max-h-48 overflow-y-auto">
                  {searchResults.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground">Sin resultados</div>
                  ) : (
                    searchResults.slice(0, 10).map((item: any) => (
                      <button
                        key={item.id}
                        onClick={() => selectProduct(item)}
                        className="w-full p-3 text-left hover:bg-muted flex justify-between items-center border-b last:border-0"
                      >
                        <span className="font-medium">
                          {item.product?.name || "Producto"}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          Stock: {item.stock} | Costo: ${parseFloat(item.averageCost || item.acquisitionCost || "0").toFixed(2)}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            {selectedItem && (
              <div className="text-sm text-green-600 bg-green-50 p-2 rounded">
                ✓ {selectedItem.product?.name || "Producto"}
              </div>
            )}
          </div>

          {selectedItem && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Stock en sistema</Label>
                  <Input value={selectedItem.stock} disabled className="bg-muted" />
                </div>
                <div>
                  <Label>Stock físico contado *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={physicalStock}
                    onChange={(e) => setPhysicalStock(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className={`p-3 rounded border ${difference !== 0 ? (difference > 0 ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200") : "bg-muted"}`}>
                <p className={`text-sm font-medium ${difference !== 0 ? (difference > 0 ? "text-green-700" : "text-red-700") : "text-muted-foreground"}`}>
                  {difference === 0
                    ? "✓ No hay diferencia"
                    : `${difference > 0 ? "Sobrante" : "Faltante"}: ${Math.abs(difference)} unidad(es) | Valor: $${diffValue.toFixed(2)}`
                  }
                </p>
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">Motivo *</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Seleccionar motivo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ADJUSTMENT_REASONS.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Notas</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notas adicionales..."
                />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>Cancelar</Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !selectedItem || difference === 0 || !reason}
          >
            {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</> : "✅ Confirmar Ajuste"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
